
import { Product } from "@/models/base";
import { Vat } from "@/models/base";
import { DossierConfigService } from "@/services/dossier-config-service";
import { SellCategory } from "@/utils/sell-category";
import Vue from "vue";
import AppLabel from "@/components/common/InfoInput.vue";
import {UserConfigService} from "@/services";
import { TaxesConfig } from "@/models/dossier-configs/taxes-config";

export default Vue.extend({
	data() {
		return {
			pricePerUnit: 0.0,
			currentFocus: "",
			currentPercentage: 21,
			computedValues: {
				bruto: 0,
				profit: 0,
				profitPercentage: 0
			},
			DossierConfigService
		};
	},
	props: {
		priceCategory: {
			type: Number,
			required: true
		},
		product: Object as () => Product,
	},
	computed: {
		config():SellCategory {
			return this.DossierConfigService.getSellCategories().getCategories()[this.priceCategory - 1];
		},
		decimals():number{
			return this.DossierConfigService.getDecimalSettings().Unit;
		},
		showPurchaseDetails():boolean{
			return UserConfigService.hasCurrentUserPermission(p=>p.CanReadProductPurchaseDetails);
		},
		taxesConfig():TaxesConfig{
			return this.DossierConfigService.getTaxesConfig();
		},
		totalTaxes():number{
			return this.product.RecupelTax + this.product.AuvibelTax + this.product.BebatTax + this.product.ReprobelTax + this.product.LeeggoedTax;
		},
	},
	watch: {
		config: {
			immediate: true,
			async handler(config:SellCategory):Promise<void> {
				if (config.CalculateViaParameters) {
					let supplier = this.product.getMainSupplier();
					if (!supplier) {
						this.pricePerUnit = 0;
						this.computedValues.profitPercentage = 0.0;
					} else {
						let percentage = config.calculateProfitBasedOnParameters(supplier.NettoPrice);
						await this.$nextTick();
						this.pricePerUnit = supplier.NettoPrice * (1 + (percentage / 100.0));
					}
				}
			}
		},
		priceCategory: {
			immediate: true,
			handler() {
				this.updateEverything();
			}
		},
		product: {
			immediate: true,
			handler() {
				this.updateEverything();
			}
		},
		"taxesConfig.ProductEditPriceIncVatIncludesTaxes"(){
			this.computeBruto();
		},
		pricePerUnit(newValue: number, oldValue: number) {
			(this.product["SellPrice" + this.priceCategory as keyof Product] as number) = newValue;
			if (this.currentFocus != "price/unit") return;
			this.computeBruto();
			this.computeProfit();
			this.computeProfitPercentage();
		},
		"computedValues.bruto"(value: number) {
			if (this.currentFocus != "bruto") return;
			this.pricePerUnit = value / ((100 + this.currentPercentage) / 100.0);
			this.computeProfit();
			this.computeProfitPercentage();
		},
		"computedValues.profit"(value: number) {
			if (this.currentFocus != "profit") return;
			let mainSupplier = this.product.getMainSupplier();
			if (!mainSupplier) return;
			this.pricePerUnit = mainSupplier.NettoPrice + value;
			this.computeProfitPercentage();
			this.computeBruto();
		},
		"computedValues.profitPercentage"(value: number) {
			if (this.currentFocus != "profitPercent") return;
			let mainSupplier = this.product.getMainSupplier();
			if (!mainSupplier) return;
			this.pricePerUnit = mainSupplier.NettoPrice * ((value + 100) / 100.0);
			this.computeProfit();
			this.computeBruto();
			this.computeProfit();
		},
		currentPercentage() {
			this.computeBruto();
		},
		"product.Vat": {
			immediate: true,
			handler(vat: Vat) {
				if (!vat) return;
				this.product.VatID = vat.ID;
				this.currentPercentage = vat.Value;
				this.computeBruto();
			}
		}
	},
	methods: {
		updateEverything() {
			let newValue = this.product["SellPrice" + this.priceCategory as keyof Product] as number;
			if (newValue == this.pricePerUnit) return;
			this.pricePerUnit = newValue;
			this.computeBruto();
			this.computeProfit();
			this.computeProfitPercentage();
		},
		computeBruto() {
			let value = this.pricePerUnit * (1.0 + (this.currentPercentage / 100.0));
			if (this.taxesConfig.ProductEditPriceIncVatIncludesTaxes){
				value += this.totalTaxes;
			}
			this.computedValues.bruto = value;
		},
		computeProfit() {
			let mainSupplier = this.product.getMainSupplier();
			if (!mainSupplier) {
				this.computedValues.profit = this.pricePerUnit;
			} else {
				this.computedValues.profit = this.pricePerUnit - mainSupplier.NettoPrice;
			}
		},
		computeProfitPercentage() {
			let mainSupplier = this.product.getMainSupplier();
			if (!mainSupplier) {
				this.computedValues.profitPercentage = 100;
			} else {
				let supPrice = mainSupplier.NettoPrice;
				this.computedValues.profitPercentage =  supPrice ? ((this.pricePerUnit - supPrice) / supPrice) * 100.0 : 0;
			}
		}
	},
	components: {
		AppLabel
	},
	i18nEx: {
		componentPrefix: "components.products"
	}
});
